import type { HelpGuide } from '@/types/help'
import { helpModules } from './modules'

// Re-export modules
export { helpModules }

export const helpGuides: HelpGuide[] = [
  // ==========================================
  // GETTING STARTED
  // ==========================================
  {
    slug: 'welcome',
    title: 'Bienvenido a Torcaza Projects',
    module: 'getting-started',
    category: 'Introducción',
    description: 'Conoce las principales funcionalidades de la plataforma',
    order: 1,
    keywords: ['inicio', 'bienvenida', 'introducción', 'empezar'],
    content: `# Bienvenido a Torcaza Projects

Torcaza Projects es tu plataforma integral para la gestión de proyectos de software. Combina las funcionalidades de un CRM, gestor de proyectos y asistente IA en una sola herramienta.

## ¿Qué puedes hacer?

### Gestión de Proyectos
- Organiza el trabajo en **proyectos**, **épicas** y **requerimientos**
- Usa el **Sprint Board** para visualizar el progreso en tiempo real
- Gestiona **releases** y versiones de tus aplicaciones

### Colaboración
- Comparte **documentos** con tu equipo y clientes
- Usa **comentarios** para discutir detalles
- Programa **reuniones** y registra decisiones

### Atención al Cliente
- Recibe **peticiones** de clientes con asistente IA
- Gestiona **tickets** de soporte con SLA
- Crea **cotizaciones** y órdenes de venta

### CRM
- Administra **prospectos** y oportunidades
- Visualiza el **pipeline** de ventas
- Convierte leads en clientes

## Navegación

Usa el **menú lateral** para acceder a las diferentes secciones. El menú se adapta a tu rol y muestra solo las opciones disponibles para ti.

## Próximos pasos

1. Explora el **Dashboard** para ver el resumen de actividad
2. Revisa tus **Proyectos** asignados
3. Configura tu **Perfil** con tus preferencias`,
  },
  {
    slug: 'navigation',
    title: 'Navegación y Menú',
    module: 'getting-started',
    category: 'Introducción',
    description: 'Aprende a moverte por la aplicación',
    order: 2,
    keywords: ['navegación', 'menú', 'sidebar', 'lateral'],
    content: `# Navegación y Menú

## Menú Lateral (Sidebar)

El menú lateral es tu principal herramienta de navegación. Contiene accesos a todos los módulos según tu rol.

### Secciones del Menú

- **Dashboard** — Vista general y métricas
- **Proyectos** — Lista de proyectos
- **Backlog** — Gestión de trabajo pendiente
- **Tickets** — Soporte y consultas
- **Calendario** — Citas y reuniones
- **Configuración** — Ajustes del sistema

### Colapsar el Menú

Haz click en el botón de hamburguesa para colapsar el menú y ganar espacio de trabajo.

## Breadcrumbs

En la parte superior de cada página verás breadcrumbs que te indican dónde estás y te permiten navegar hacia atrás.

## Atajos

- **Ctrl + K** — Búsqueda global
- **?** — Abrir Centro de Ayuda`,
  },

  // ==========================================
  // DASHBOARD
  // ==========================================
  {
    slug: 'dashboard-overview',
    title: 'Vista General del Dashboard',
    module: 'dashboard',
    category: 'General',
    description: 'Conoce los widgets y métricas del dashboard principal',
    order: 1,
    keywords: ['dashboard', 'inicio', 'métricas', 'widgets', 'resumen'],
    content: `# Dashboard

El Dashboard es tu punto de partida al iniciar sesión. Muestra un resumen de la actividad relevante para tu rol.

## Widgets Disponibles

### Proyectos Activos
Muestra los proyectos en desarrollo con su estado actual y progreso.

### Tickets Pendientes
Lista de tickets que requieren tu atención, ordenados por prioridad.

### Próximas Reuniones
Calendario resumido con las reuniones de los próximos días.

### Actividad Reciente
Timeline de las últimas acciones en el sistema.

## Personalización

El dashboard se adapta automáticamente a tu rol:
- **Admin/Operator** — Ve métricas de todos los proyectos
- **Cliente** — Ve solo sus proyectos y tickets`,
  },

  // ==========================================
  // PROJECTS
  // ==========================================
  {
    slug: 'projects-list',
    title: 'Lista de Proyectos',
    module: 'projects',
    category: 'General',
    description: 'Cómo ver y filtrar proyectos',
    order: 1,
    keywords: ['proyectos', 'lista', 'filtrar', 'buscar'],
    actionLinks: [
      { label: 'Ver Proyectos', route: '/projects', icon: 'FolderKanban', variant: 'default' },
    ],
    content: `# Lista de Proyectos

La vista de proyectos muestra todos los proyectos a los que tienes acceso.

## Filtros Disponibles

- **Estado** — Planificación, En desarrollo, Completado, etc.
- **Organización** — Filtrar por cliente (solo admin/operator)
- **Búsqueda** — Por nombre o key del proyecto

## Información de cada Proyecto

- **Nombre y Key** — Identificador único (ej: TPL)
- **Estado** — Badge con color según estado
- **Organización** — Cliente asociado
- **Épicas/Requerimientos** — Contadores de items

## Acciones

- **Click en proyecto** — Ver detalle completo
- **Nuevo Proyecto** — Crear proyecto (solo admin)`,
  },
  {
    slug: 'projects-detail',
    title: 'Detalle de Proyecto',
    module: 'projects',
    category: 'General',
    description: 'Todas las secciones del detalle de un proyecto',
    order: 2,
    keywords: ['proyecto', 'detalle', 'tabs', 'secciones'],
    content: `# Detalle de Proyecto

La vista de detalle muestra toda la información del proyecto organizada en tabs.

## Tabs Disponibles

### Overview
Información general: descripción, fechas, equipo asignado.

### Épicas
Lista de épicas del proyecto con su progreso.

### Requerimientos
Todos los requerimientos organizados por épica o estado.

### Documentos
Documentación del proyecto: alcance, arquitectura, manuales.

### Aplicaciones
Apps o sistemas que componen el proyecto.

### Reuniones
Historial de reuniones con notas y action items.

## Acciones Rápidas

- **Editar** — Modificar información del proyecto
- **Nueva Épica** — Crear épica
- **Nuevo Requerimiento** — Crear requerimiento
- **Nuevo Documento** — Crear documento`,
  },

  // ==========================================
  // BACKLOG & SPRINTS
  // ==========================================
  {
    slug: 'backlog-overview',
    title: 'Vista de Backlog',
    module: 'backlog',
    category: 'Backlog',
    description: 'Gestiona el trabajo pendiente',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 1,
    keywords: ['backlog', 'pendiente', 'lista', 'priorizar'],
    actionLinks: [
      { label: 'Ir al Backlog', route: '/backlog', icon: 'ListTodo', variant: 'default' },
    ],
    content: `# Backlog

El Backlog contiene todos los requerimientos pendientes de asignar a un sprint.

## Vista de Lista

Los items se muestran ordenados por prioridad:
- 🔴 **Critical** — Urgente, bloquea otras tareas
- 🟠 **High** — Alta prioridad
- 🟡 **Medium** — Prioridad normal
- 🟢 **Low** — Puede esperar

## Filtros

- **Proyecto** — Filtrar por proyecto específico
- **Tipo** — Story, Task, Bug, Spike
- **Épica** — Filtrar por épica
- **Estado** — Draft, Ready, Approved

## Acciones

- **Asignar a Sprint** — Mover item al sprint activo
- **Editar** — Modificar el requerimiento
- **Priorizar** — Cambiar orden de prioridad`,
  },
  {
    slug: 'sprint-board-overview',
    title: 'Vista Kanban del Sprint',
    module: 'backlog',
    category: 'Sprint Board',
    description: 'Aprende a usar el tablero Kanban para gestionar tu sprint',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 10,
    keywords: ['sprint', 'kanban', 'tablero', 'board', 'columnas'],
    relatedGuides: ['sprint-board-drag-drop', 'sprint-board-filters'],
    actionLinks: [
      { label: 'Abrir Sprint Board', route: '/backlog/sprint', icon: 'LayoutDashboard', variant: 'default' },
      { label: 'Ver Backlog', route: '/backlog', icon: 'ListTodo', variant: 'outline' },
    ],
    content: `# Sprint Board

El Sprint Board es tu centro de comando para gestionar el trabajo del sprint actual. Usa una vista Kanban para visualizar el flujo de trabajo.

## Columnas

### To Do
Tareas pendientes de iniciar. Ya están en el sprint pero no se ha comenzado el trabajo.

### In Progress
Trabajo en curso. Alguien está activamente trabajando en esta tarea.

### Done
Tareas completadas. El trabajo está terminado y verificado.

## Tarjetas

Cada tarjeta representa un requerimiento y muestra:

- **Key** — Identificador único (ej: TPL-15)
- **Título** — Nombre del requerimiento
- **Labels** — Etiquetas de categoría (badges de colores)
- **🔒 Bloqueo** — Indica si está bloqueado por otra tarea
- **📅 Fecha límite** — Se muestra en rojo si está vencida

## Métricas

En la parte superior puedes ver:
- **Items en sprint** — Total de tareas
- **Completados** — Tareas terminadas
- **Story Points** — Puntos totales vs completados`,
  },
  {
    slug: 'sprint-board-drag-drop',
    title: 'Mover Tarjetas',
    module: 'backlog',
    category: 'Sprint Board',
    description: 'Cómo arrastrar tarjetas entre columnas',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 11,
    keywords: ['drag', 'drop', 'mover', 'arrastrar', 'columna'],
    relatedGuides: ['sprint-board-overview', 'sprint-board-filters'],
    content: `# Mover Tarjetas

Puedes mover tarjetas entre columnas usando drag & drop para actualizar su estado.

## Cómo Mover

1. **Click y mantén** presionado sobre una tarjeta
2. **Arrastra** hacia la columna destino
3. Una **línea azul** indica dónde se insertará
4. **Suelta** en la posición deseada

## Reordenar

También puedes reordenar tarjetas dentro de la misma columna:
- Arrastra una tarjeta arriba o abajo
- La posición se guarda automáticamente

## Soltar en Columna Vacía

Puedes soltar directamente en cualquier parte de la columna, no solo entre tarjetas existentes.

## Cambios Automáticos

Al mover una tarjeta:
- El **estado de desarrollo** se actualiza automáticamente
- El cambio se **guarda en Firebase** inmediatamente
- Otros usuarios ven el cambio en tiempo real`,
  },
  {
    slug: 'sprint-board-filters',
    title: 'Filtrar por Etiqueta',
    module: 'backlog',
    category: 'Sprint Board',
    description: 'Cómo filtrar las tarjetas del board por labels',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 12,
    keywords: ['filtro', 'etiqueta', 'label', 'buscar', 'tag'],
    relatedGuides: ['sprint-board-overview', 'sprint-board-drag-drop'],
    content: `# Filtrar por Etiqueta

Usa los filtros para ver solo las tarjetas relevantes en el Sprint Board.

## Cómo Filtrar

1. Ubica el **selector de etiquetas** arriba del board
2. **Click** en el dropdown
3. **Selecciona** una o más etiquetas
4. El board muestra solo tarjetas con esas etiquetas

## Múltiples Etiquetas

Puedes seleccionar varias etiquetas a la vez:
- Se muestran tarjetas que tengan **cualquiera** de las etiquetas seleccionadas

## Limpiar Filtros

Para ver todas las tarjetas nuevamente:
- Click en **"Limpiar"** o
- Deselecciona todas las etiquetas

## Etiquetas Comunes

- **frontend** — Trabajo de interfaz
- **backend** — Trabajo de servidor
- **bug** — Corrección de errores
- **enhancement** — Mejoras
- **documentation** — Documentación`,
  },

  // ==========================================
  // REQUIREMENTS
  // ==========================================
  {
    slug: 'requirements-overview',
    title: 'Qué son los Requerimientos',
    module: 'requirements',
    category: 'General',
    description: 'Introducción a los requerimientos en Torcaza Projects',
    order: 1,
    keywords: ['requerimiento', 'historia', 'user story', 'tarea'],
    content: `# Requerimientos

Los requerimientos son las unidades de trabajo en Torcaza Projects. Representan funcionalidades, tareas o bugs a resolver.

## Tipos de Requerimientos

### Story (Historia de Usuario)
Funcionalidad descrita desde la perspectiva del usuario.
> "Como usuario quiero X para lograr Y"

### Task (Tarea)
Trabajo técnico o administrativo que no es una historia.

### Bug
Error o problema a corregir.

### Spike
Investigación o prototipo para reducir incertidumbre.

### Subtask
Tarea hija de otro requerimiento.

## Campos Principales

- **Título** — Nombre descriptivo
- **Descripción** — Detalle del requerimiento
- **User Story** — Formato "Como... quiero... para..."
- **Criterios de Aceptación** — Condiciones para dar por terminado
- **Prioridad** — Critical, High, Medium, Low
- **Story Points** — Estimación de esfuerzo (Fibonacci)`,
  },
  {
    slug: 'requirements-states',
    title: 'Estados de Requerimientos',
    module: 'requirements',
    category: 'General',
    description: 'Flujo de estados de definición y desarrollo',
    order: 2,
    keywords: ['estado', 'status', 'flujo', 'workflow'],
    content: `# Estados de Requerimientos

Los requerimientos tienen dos tipos de estado que se gestionan de forma independiente.

## Estados de Definición

Indican qué tan completa está la especificación:

| Estado | Descripción |
|--------|-------------|
| **Draft** | Borrador inicial, incompleto |
| **Ready** | Listo para revisión |
| **Needs Info** | Falta información del cliente |
| **Approved** | Aprobado para desarrollo |
| **Rejected** | Rechazado o descartado |

## Estados de Desarrollo

Indican el progreso del trabajo:

| Estado | Descripción |
|--------|-------------|
| **Backlog** | En el backlog, sin asignar |
| **To Do** | Asignado al sprint, pendiente |
| **In Progress** | En desarrollo activo |
| **Code Review** | En revisión de código |
| **Testing** | En pruebas |
| **Done** | Completado |
| **Blocked** | Bloqueado por dependencia |

## Flujo Típico

1. Se crea en **Draft**
2. Se completa y pasa a **Ready**
3. PO aprueba → **Approved**
4. Se asigna a sprint → **To Do**
5. Dev toma la tarea → **In Progress**
6. Termina código → **Code Review**
7. Pasa review → **Testing**
8. QA aprueba → **Done**`,
  },

  // ==========================================
  // TICKETS
  // ==========================================
  {
    slug: 'tickets-create',
    title: 'Crear un Ticket',
    module: 'tickets',
    category: 'General',
    description: 'Cómo reportar un problema o hacer una consulta',
    order: 1,
    keywords: ['ticket', 'crear', 'soporte', 'problema', 'bug'],
    actionLinks: [
      { label: 'Ir a Tickets', route: '/tickets', icon: 'Ticket', variant: 'default' },
    ],
    content: `# Crear un Ticket

Los tickets sirven para reportar problemas, hacer consultas o solicitar ayuda.

## Pasos

1. Ve a **Tickets** en el menú lateral
2. Click en **"Nuevo Ticket"**
3. Completa el formulario:
   - **Tipo**: Bug, Soporte, Consulta, Mejora
   - **Proyecto**: Selecciona el proyecto relacionado
   - **Título**: Resumen breve del problema
   - **Descripción**: Detalle completo
   - **Prioridad**: Según urgencia
4. Click en **"Crear"**

## Tips para un Buen Ticket

- **Sé específico** — Describe exactamente qué pasó
- **Incluye pasos** — Cómo reproducir el problema
- **Adjunta capturas** — Una imagen vale más que mil palabras
- **Indica el impacto** — ¿Bloquea tu trabajo?

## Después de Crear

Recibirás notificaciones cuando el equipo responda o actualice el estado.`,
  },
  {
    slug: 'tickets-status',
    title: 'Estados de Tickets',
    module: 'tickets',
    category: 'General',
    description: 'Entender el flujo de estados de un ticket',
    order: 2,
    keywords: ['ticket', 'estado', 'status', 'abierto', 'cerrado'],
    content: `# Estados de Tickets

Los tickets pasan por diferentes estados durante su ciclo de vida.

## Estados

| Estado | Significado |
|--------|-------------|
| **Open** | Nuevo, pendiente de atención |
| **In Progress** | El equipo está trabajando en él |
| **Waiting Customer** | Esperando tu respuesta |
| **Waiting Internal** | Esperando respuesta interna |
| **Resolved** | Solucionado, pendiente confirmación |
| **Closed** | Cerrado definitivamente |

## SLA (Acuerdos de Nivel de Servicio)

Según la prioridad, hay tiempos máximos de respuesta:

| Prioridad | Primera Respuesta | Resolución |
|-----------|------------------|------------|
| Critical | 1 hora | 4 horas |
| High | 4 horas | 24 horas |
| Medium | 8 horas | 48 horas |
| Low | 24 horas | 1 semana |

## Indicadores

- 🟢 **En tiempo** — Dentro del SLA
- 🟡 **Por vencer** — Cerca del límite
- 🔴 **Vencido** — Excedió el SLA`,
  },

  // ==========================================
  // DOCUMENTS
  // ==========================================
  {
    slug: 'documents-create',
    title: 'Crear Documento',
    module: 'documents',
    category: 'General',
    description: 'Cómo crear documentos markdown',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 1,
    keywords: ['documento', 'crear', 'markdown', 'nuevo'],
    actionLinks: [
      { label: 'Ver Documentos', route: '/documents', icon: 'FileText', variant: 'default' },
    ],
    content: `# Crear Documento

Los documentos en Torcaza Projects usan formato Markdown para fácil edición y lectura.

## Pasos

1. Navega a la entidad (proyecto, aplicación, etc.)
2. Ve a la tab de **Documentos**
3. Click en **"Nuevo Documento"**
4. Completa:
   - **Título** — Nombre descriptivo
   - **Tipo** — Alcance, Arquitectura, Specs, Manual, Otro
   - **Contenido** — Editor markdown con preview
5. Click en **"Guardar"**

## Editor Markdown

El editor tiene dos paneles:
- **Izquierda** — Escribes el código markdown
- **Derecha** — Preview en tiempo real

## Tipos de Documento

| Tipo | Uso |
|------|-----|
| **Scope** | Alcance del proyecto |
| **Architecture** | Diseño técnico |
| **Specifications** | Especificaciones detalladas |
| **Manual** | Guías de usuario |
| **Other** | Cualquier otro documento |

## Minimizar

Si necesitas consultar otra información, puedes **minimizar** el documento. Aparecerá como widget flotante y podrás restaurarlo después.`,
  },
  {
    slug: 'documents-share',
    title: 'Compartir Documento',
    module: 'documents',
    category: 'Compartir',
    description: 'Cómo compartir documentos con personas externas',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 10,
    keywords: ['compartir', 'share', 'link', 'externo', 'público'],
    relatedGuides: ['documents-mfa'],
    content: `# Compartir Documento

Comparte documentos con clientes o colaboradores externos mediante un link.

## Modos de Acceso

### 🌐 Público
Cualquier persona con el link puede ver el documento. Útil para documentación general.

### 🔒 Restringido
Solo emails autorizados pueden acceder. Debes agregar cada email a la lista de permitidos.

## Pasos para Compartir

1. Abre el documento
2. Click en el botón **"Compartir"** (icono de compartir)
3. Selecciona el **modo de acceso**
4. Si es restringido:
   - Escribe cada email y click en "Agregar"
   - Los emails aparecen como badges
5. Click en **"Guardar"**
6. **Copia el link** generado

## Opciones Adicionales

- **Permitir comentarios** — Los visitantes pueden dejar comentarios
- **MFA por email** — Requiere código de verificación (ver guía relacionada)

## Revocar Acceso

Para dejar de compartir:
1. Abre la configuración de compartir
2. Selecciona **"No compartir"**
3. Guarda los cambios

El link anterior dejará de funcionar.`,
  },
  {
    slug: 'documents-mfa',
    title: 'MFA por Email en Documentos',
    module: 'documents',
    category: 'Compartir',
    description: 'Verificación adicional para documentos restringidos',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 11,
    keywords: ['mfa', 'verificación', 'código', 'seguridad', '2fa'],
    relatedGuides: ['documents-share'],
    content: `# MFA por Email

Agrega una capa extra de seguridad a documentos restringidos requiriendo un código de verificación.

## Cómo Funciona

1. El visitante ingresa su email (debe estar en la lista)
2. Recibe un **código de 6 dígitos** por email
3. Ingresa el código en la página
4. Si es correcto, puede ver el documento
5. El código **expira en 10 minutos**

## Activar MFA

1. Abre la configuración de compartir
2. Selecciona modo **Restringido**
3. Agrega los emails autorizados
4. Click en el **icono de escudo** (🛡️) junto al email
5. El icono se pone azul indicando MFA activo
6. Guarda los cambios

## Cuándo Usarlo

- Documentos con información **confidencial**
- Datos sensibles de **proyectos**
- Cuando necesitas **verificar la identidad** del visitante
- Cumplimiento de **políticas de seguridad**

## Notas

- El código solo se puede usar **una vez**
- Hay un límite de **5 códigos por hora** por email
- El visitante puede solicitar **reenvío** después de 60 segundos`,
  },

  // ==========================================
  // PROFILE
  // ==========================================
  {
    slug: 'profile-overview',
    title: 'Tu Perfil',
    module: 'profile',
    category: 'General',
    description: 'Vista general de la configuración de perfil',
    order: 1,
    keywords: ['perfil', 'cuenta', 'usuario', 'configuración'],
    content: `# Tu Perfil

La página de perfil te permite ver y editar tu información personal y preferencias.

## Secciones

### Información Personal
- **Nombre** — Cómo apareces en la aplicación
- **Email** — Tu correo (no editable)
- **Teléfono** — Número de contacto

### Preferencias
- **Tema** — Claro u oscuro
- **Idioma** — Español o inglés

### Seguridad
- **Cambiar contraseña** — Actualiza tu clave de acceso

### Información de Cuenta
- **Rol** — Tu rol en el sistema
- **Miembro desde** — Fecha de registro

## Acceder al Perfil

1. Click en tu **avatar** en la esquina superior derecha
2. Selecciona **"Perfil"**

O directamente desde el menú lateral.`,
  },
  {
    slug: 'profile-theme',
    title: 'Cambiar Tema (Claro/Oscuro)',
    module: 'profile',
    category: 'Preferencias',
    description: 'Cómo cambiar entre tema claro y oscuro',
    order: 10,
    keywords: ['tema', 'oscuro', 'claro', 'dark', 'light', 'modo', 'apariencia'],
    content: `# Cambiar Tema

Personaliza la apariencia de Torcaza Projects según tu preferencia.

## Opciones

### ☀️ Tema Claro
- Fondo blanco/gris claro
- Ideal para uso diurno
- Mejor legibilidad con luz ambiental

### 🌙 Tema Oscuro
- Fondo oscuro/negro
- Reduce fatiga visual en ambientes oscuros
- Ahorra batería en pantallas OLED

## Cómo Cambiar

1. Ve a **Perfil** en el menú lateral
2. En la sección **Preferencias**
3. Busca la opción **"Tema"**
4. Selecciona **Claro** u **Oscuro**
5. El cambio se aplica **inmediatamente**

## Sincronización

Tu preferencia se guarda en tu cuenta y se sincroniza en todos los dispositivos donde inicies sesión.`,
  },
  {
    slug: 'profile-language',
    title: 'Cambiar Idioma',
    module: 'profile',
    category: 'Preferencias',
    description: 'Cómo cambiar el idioma de la interfaz',
    order: 11,
    keywords: ['idioma', 'español', 'inglés', 'language', 'traducción'],
    content: `# Cambiar Idioma

La interfaz está disponible en español e inglés.

## Idiomas Disponibles

- 🇪🇸 **Español** — Idioma por defecto
- 🇺🇸 **English** — Interfaz en inglés

## Cómo Cambiar

1. Ve a **Perfil** en el menú lateral
2. En la sección **Preferencias**
3. Busca la opción **"Idioma"**
4. Selecciona el idioma deseado
5. La interfaz se actualiza **inmediatamente**

## Notas

- El contenido creado por usuarios (documentos, comentarios) permanece en su idioma original
- Los emails del sistema se envían en el idioma configurado
- Tu preferencia se guarda en tu perfil`,
  },
  {
    slug: 'profile-password',
    title: 'Cambiar Contraseña',
    module: 'profile',
    category: 'Seguridad',
    description: 'Cómo actualizar tu contraseña de acceso',
    order: 20,
    keywords: ['contraseña', 'password', 'seguridad', 'cambiar', 'clave'],
    content: `# Cambiar Contraseña

Mantén tu cuenta segura actualizando tu contraseña periódicamente.

## Requisitos de Contraseña

- Mínimo **8 caracteres**
- Debe ser **diferente** a la actual
- Se recomienda usar combinación de letras, números y símbolos

## Pasos

1. Ve a **Perfil** en el menú lateral
2. En la sección **Seguridad**
3. Click en **"Cambiar contraseña"**
4. Ingresa tu **contraseña actual**
5. Ingresa la **nueva contraseña**
6. **Confirma** la nueva contraseña
7. Click en **"Guardar"**

## Si Olvidaste tu Contraseña

Si no recuerdas tu contraseña actual:

1. Cierra sesión
2. En la página de login, click en **"¿Olvidaste tu contraseña?"**
3. Ingresa tu email
4. Recibirás un link para crear una nueva contraseña

## Recomendaciones

- No uses la misma contraseña en múltiples sitios
- Evita información personal obvia (fechas, nombres)
- Considera usar un gestor de contraseñas`,
  },

  // ==========================================
  // PETITIONS
  // ==========================================
  {
    slug: 'petitions-create',
    title: 'Crear una Petición',
    module: 'petitions',
    category: 'General',
    description: 'Cómo solicitar nuevas funcionalidades o cambios',
    order: 1,
    keywords: ['petición', 'solicitud', 'crear', 'nueva', 'request'],
    content: `# Crear una Petición

Las peticiones son solicitudes de nuevas funcionalidades, mejoras o cambios que envías al equipo de desarrollo.

## ¿Cuándo crear una petición?

- Necesitas una **nueva funcionalidad**
- Quieres **mejorar** algo existente
- Tienes una **idea** para el producto
- Necesitas un **cambio** en el comportamiento actual

## Pasos

1. Ve a **Peticiones** en el menú lateral
2. Click en **"Nueva Petición"**
3. Describe tu solicitud:
   - **Título** — Resumen breve
   - **Descripción** — Detalle de lo que necesitas
   - **Categoría** — Nueva función, Mejora, Cambio, etc.
   - **Prioridad** — Qué tan urgente es

## Usar el Asistente IA

Si no sabes cómo estructurar tu solicitud:

1. Click en **"Usar Asistente"**
2. Describe tu necesidad en el chat
3. La IA te ayudará a estructurar la petición
4. Genera automáticamente título, descripción y categoría

## Después de Enviar

- Recibirás notificaciones de cambios de estado
- El equipo puede pedir más información
- Si se aprueba, se convierte en requerimiento`,
  },
  {
    slug: 'petitions-ai-assistant',
    title: 'Asistente IA para Peticiones',
    module: 'petitions',
    category: 'Asistente',
    description: 'Cómo usar el chat con IA para crear peticiones',
    order: 10,
    keywords: ['ia', 'asistente', 'chat', 'inteligencia artificial', 'ai'],
    content: `# Asistente IA para Peticiones

El asistente IA te ayuda a estructurar tus solicitudes de manera clara y completa.

## Cómo Funciona

1. Inicias una conversación describiendo tu necesidad
2. La IA hace preguntas para entender mejor
3. Te ayuda a refinar los detalles
4. Genera un borrador estructurado

## Ejemplo de Conversación

**Tú:** Necesito que los usuarios puedan exportar reportes a PDF

**IA:** Entiendo. ¿Qué reportes específicamente necesitas exportar? ¿Hay algún formato o información particular que deba incluir?

**Tú:** Los reportes de progreso del proyecto, con gráficas y métricas

**IA:** Perfecto. Voy a generar un borrador de petición con esos detalles...

## Generar Borrador

Una vez que la IA tiene suficiente información:

1. Click en **"Generar Borrador"**
2. Revisa el título, descripción y categoría propuestos
3. Edita si es necesario
4. Click en **"Crear Petición"**

## Tips

- Sé específico sobre lo que necesitas
- Explica el **por qué** además del **qué**
- Menciona casos de uso concretos
- Indica si hay urgencia o fechas límite`,
  },

  // ==========================================
  // ORGANIZATIONS
  // ==========================================
  {
    slug: 'organizations-overview',
    title: 'Gestión de Organizaciones',
    module: 'organizations',
    category: 'General',
    description: 'Cómo funcionan las organizaciones en Torcaza Projects',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 1,
    keywords: ['organización', 'cliente', 'empresa', 'cuenta'],
    content: `# Organizaciones

Las organizaciones representan a tus clientes o empresas con las que trabajas.

## Qué es una Organización

Una organización agrupa:
- **Proyectos** — Trabajos para ese cliente
- **Usuarios** — Personas de esa empresa con acceso
- **Documentos** — Contratos, especificaciones, etc.
- **Historial** — Reuniones, tickets, cotizaciones

## Lista de Organizaciones

Ve a **Organizaciones** en el menú para ver todas:
- **Nombre** y logo de la empresa
- **Contacto principal** — Persona de referencia
- **Proyectos activos** — Cantidad de proyectos en curso
- **Estado** — Activo, Inactivo, Prospecto

## Crear Organización

1. Click en **"Nueva Organización"**
2. Completa la información básica:
   - Nombre de la empresa
   - Industria
   - Tamaño (empleados)
3. Agrega contacto principal
4. Guarda

## Desde Prospectos

También puedes convertir un prospecto en organización desde el CRM cuando se cierra una venta.`,
  },
  {
    slug: 'organizations-detail',
    title: 'Detalle de Organización',
    module: 'organizations',
    category: 'General',
    description: 'Toda la información de un cliente en un solo lugar',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 2,
    keywords: ['organización', 'detalle', 'cliente', 'información'],
    content: `# Detalle de Organización

La vista de detalle centraliza toda la información de un cliente.

## Tabs Disponibles

### Overview
Información general: datos de contacto, industria, notas.

### Proyectos
Lista de proyectos de la organización con estado y progreso.

### Contactos
Personas de la organización con acceso al sistema.

### Documentos
Contratos, propuestas y documentación compartida.

### Reuniones
Historial de reuniones con la organización.

### Tickets
Tickets de soporte abiertos por la organización.

### Cotizaciones
Propuestas comerciales enviadas.

### Facturación
Órdenes de venta y estado de pagos.

## Acciones

- **Editar** — Modificar información
- **Nuevo Proyecto** — Crear proyecto para este cliente
- **Nueva Reunión** — Agendar reunión
- **Nueva Cotización** — Crear propuesta comercial`,
  },

  // ==========================================
  // EPICS
  // ==========================================
  {
    slug: 'epics-overview',
    title: 'Qué son las Épicas',
    module: 'epics',
    category: 'General',
    description: 'Introducción a las épicas como agrupadores de trabajo',
    order: 1,
    keywords: ['épica', 'epic', 'agrupador', 'módulo', 'feature'],
    content: `# Épicas

Las épicas son agrupadores de requerimientos que representan funcionalidades grandes o módulos del sistema.

## Propósito

Una épica agrupa requerimientos relacionados para:
- **Organizar** el trabajo por funcionalidad
- **Medir** progreso de features completas
- **Planificar** entregas incrementales
- **Comunicar** avances a stakeholders

## Ejemplo

**Épica:** Módulo de Autenticación
- Req: Login con email/password
- Req: Recuperar contraseña
- Req: Login con Google
- Req: Logout y manejo de sesión
- Req: Roles y permisos básicos

## Campos de una Épica

- **Título** — Nombre descriptivo
- **Descripción** — Objetivo de la funcionalidad
- **Proyecto** — Proyecto al que pertenece
- **Estado** — Draft, In Progress, Done
- **Progreso** — % calculado de reqs completados

## Crear Épica

1. Ve al detalle del proyecto
2. Tab **"Épicas"**
3. Click **"Nueva Épica"**
4. Completa título y descripción
5. Guarda`,
  },
  {
    slug: 'epics-progress',
    title: 'Progreso de Épicas',
    module: 'epics',
    category: 'Seguimiento',
    description: 'Cómo medir el avance de una épica',
    order: 2,
    keywords: ['épica', 'progreso', 'avance', 'porcentaje', 'tracking'],
    content: `# Progreso de Épicas

El progreso de una épica se calcula automáticamente según sus requerimientos.

## Cómo se Calcula

El porcentaje refleja cuántos requerimientos están en estado **Done** respecto al total:

\`\`\`
Progreso = (Reqs Done / Total Reqs) × 100
\`\`\`

## Barra de Progreso

En la lista de épicas verás una barra visual:
- 🟢 **Verde** — Requerimientos completados
- 🔵 **Azul** — Requerimientos en progreso
- ⬜ **Gris** — Requerimientos pendientes

## Estados de la Épica

| Estado | Significado |
|--------|-------------|
| **Draft** | En definición, sin reqs asignados |
| **In Progress** | Al menos un req en desarrollo |
| **Done** | Todos los reqs completados |

## Tips

- Divide épicas muy grandes (más de 15-20 reqs)
- Revisa el progreso en las daily meetings
- Una épica Done puede incluirse en un Release`,
  },

  // ==========================================
  // RELEASES
  // ==========================================
  {
    slug: 'releases-overview',
    title: 'Gestión de Releases',
    module: 'releases',
    category: 'General',
    description: 'Cómo planificar y ejecutar releases',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 1,
    keywords: ['release', 'versión', 'despliegue', 'deploy', 'entrega'],
    content: `# Releases

Los releases representan versiones desplegables del software con funcionalidades completadas.

## Qué es un Release

Un release agrupa:
- **Requerimientos** completados que se incluyen
- **Fecha objetivo** de despliegue
- **Notas de versión** para comunicar cambios
- **Estado** del proceso de release

## Ciclo de Vida

1. **Planning** — Se define alcance y fecha
2. **In Progress** — Desarrollo activo
3. **Code Freeze** — Sin nuevos cambios
4. **Testing** — QA en ambiente de staging
5. **Ready** — Listo para producción
6. **Released** — Desplegado exitosamente

## Crear Release

1. Ve al proyecto → Tab **Releases**
2. Click **"Nuevo Release"**
3. Define:
   - Número de versión (ej: 1.2.0)
   - Tipo: Major, Minor, Patch, Hotfix
   - Fecha objetivo
4. Selecciona requerimientos a incluir
5. Guarda

## Semantic Versioning

Recomendamos seguir semver:
- **Major** (1.0.0) — Cambios incompatibles
- **Minor** (1.1.0) — Nueva funcionalidad compatible
- **Patch** (1.1.1) — Correcciones de bugs`,
  },
  {
    slug: 'releases-notes',
    title: 'Notas de Release',
    module: 'releases',
    category: 'Comunicación',
    description: 'Cómo documentar los cambios en cada versión',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 2,
    keywords: ['release', 'notas', 'changelog', 'cambios', 'documentar'],
    content: `# Notas de Release

Las notas de release documentan los cambios incluidos en cada versión.

## Estructura Recomendada

### ✨ Nuevas Funcionalidades
- Descripción de features nuevas
- Beneficios para el usuario

### 🐛 Correcciones
- Bugs resueltos
- Problemas de rendimiento

### ⚡ Mejoras
- Optimizaciones
- Cambios de UX

### ⚠️ Cambios Importantes
- Breaking changes
- Deprecaciones
- Migraciones necesarias

## Generar Notas

1. Abre el release
2. Click **"Generar Notas"**
3. El sistema crea un borrador basado en los requerimientos
4. Edita y personaliza el texto
5. Guarda

## Publicar

Las notas se publican automáticamente en el Historial de Cambios cuando el release pasa a estado **Released**.`,
  },

  // ==========================================
  // CRM
  // ==========================================
  {
    slug: 'crm-pipeline',
    title: 'Pipeline de Ventas',
    module: 'crm',
    category: 'General',
    description: 'Gestiona oportunidades de venta con el pipeline',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 1,
    keywords: ['crm', 'pipeline', 'ventas', 'oportunidades', 'leads'],
    content: `# Pipeline de Ventas

El pipeline visualiza tus oportunidades comerciales en diferentes etapas.

## Etapas del Pipeline

### Lead
Contacto inicial, interés detectado.

### Qualified
Lead calificado, hay potencial real.

### Proposal
Propuesta enviada, esperando respuesta.

### Negotiation
Negociando términos y condiciones.

### Won
Venta cerrada exitosamente. 🎉

### Lost
Oportunidad perdida. Registra el motivo.

## Vista Kanban

El pipeline se muestra como tablero Kanban:
- Arrastra prospectos entre columnas
- Ve el valor total por etapa
- Identifica cuellos de botella

## Métricas

- **Tasa de conversión** — % de leads que llegan a Won
- **Ciclo de venta** — Tiempo promedio lead → won
- **Valor del pipeline** — Suma de oportunidades activas

## Tips

- Actualiza los prospectos regularmente
- Registra notas de cada interacción
- Usa etiquetas para segmentar`,
  },
  {
    slug: 'crm-prospects',
    title: 'Gestión de Prospectos',
    module: 'crm',
    category: 'Prospectos',
    description: 'Cómo administrar prospectos y leads',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 2,
    keywords: ['prospecto', 'lead', 'cliente', 'potencial', 'oportunidad'],
    content: `# Gestión de Prospectos

Los prospectos son clientes potenciales que aún no han comprado.

## Crear Prospecto

1. Ve a **CRM** → **Prospectos**
2. Click **"Nuevo Prospecto"**
3. Completa:
   - Nombre de la empresa
   - Contacto principal
   - Fuente (cómo llegó)
   - Valor estimado
4. Guarda

## Información del Prospecto

### Datos Básicos
- Empresa, contacto, email, teléfono
- Industria y tamaño

### Calificación
- Presupuesto (¿tiene recursos?)
- Autoridad (¿puede decidir?)
- Necesidad (¿resolvemos su problema?)
- Timeline (¿cuándo compraría?)

### Actividad
- Historial de interacciones
- Próximos pasos
- Notas del equipo

## Convertir a Organización

Cuando se cierra la venta:
1. Abre el prospecto
2. Click **"Convertir a Organización"**
3. Se crea la organización con los datos
4. El prospecto se marca como "Won"`,
  },

  // ==========================================
  // CALENDAR
  // ==========================================
  {
    slug: 'calendar-overview',
    title: 'Vista de Calendario',
    module: 'calendar',
    category: 'General',
    description: 'Cómo usar el calendario para ver citas y reuniones',
    order: 1,
    keywords: ['calendario', 'citas', 'agenda', 'eventos', 'fechas'],
    content: `# Calendario

El calendario muestra todas tus citas, reuniones y eventos programados.

## Vistas Disponibles

### Mes
Vista completa del mes con eventos como puntos o barras.

### Semana
Vista detallada de la semana con franjas horarias.

### Día
Vista de un día específico con todas las horas.

### Agenda
Lista cronológica de próximos eventos.

## Colores

Los eventos se colorean según tipo:
- 🔵 **Azul** — Reuniones de proyecto
- 🟢 **Verde** — Citas con clientes
- 🟡 **Amarillo** — Recordatorios
- 🔴 **Rojo** — Deadlines

## Navegación

- **Flechas** — Mes/semana anterior o siguiente
- **Hoy** — Volver a la fecha actual
- **Click en fecha** — Ver detalle de ese día
- **Click en evento** — Ver detalle del evento

## Sincronización

El calendario se sincroniza en tiempo real. Los cambios de otros usuarios aparecen automáticamente.`,
  },
  {
    slug: 'calendar-appointments',
    title: 'Crear Citas',
    module: 'calendar',
    category: 'Citas',
    description: 'Cómo agendar citas con clientes',
    order: 2,
    keywords: ['cita', 'agendar', 'programar', 'appointment', 'cliente'],
    content: `# Crear Citas

Las citas son eventos agendados con clientes u otras partes externas.

## Crear Cita

1. Click en una fecha/hora del calendario
2. O click en **"Nueva Cita"**
3. Completa:
   - **Título** — Motivo de la cita
   - **Fecha y hora** — Inicio y fin
   - **Organización** — Cliente relacionado
   - **Participantes** — Personas invitadas
   - **Ubicación** — Lugar o link de videollamada
   - **Notas** — Agenda o puntos a tratar
4. Guarda

## Estados

| Estado | Significado |
|--------|-------------|
| **Scheduled** | Programada |
| **Confirmed** | Cliente confirmó asistencia |
| **Cancelled** | Cancelada |
| **Completed** | Realizada |
| **No Show** | Cliente no asistió |

## Recordatorios

El sistema envía recordatorios automáticos:
- 24 horas antes por email
- 1 hora antes por notificación

## Después de la Cita

Marca como **Completed** y agrega notas de lo conversado. Puedes crear tareas de seguimiento desde la cita.`,
  },

  // ==========================================
  // MEETINGS
  // ==========================================
  {
    slug: 'meetings-overview',
    title: 'Reuniones de Proyecto',
    module: 'meetings',
    category: 'General',
    description: 'Cómo gestionar reuniones de equipo',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 1,
    keywords: ['reunión', 'meeting', 'equipo', 'proyecto', 'minuta'],
    content: `# Reuniones de Proyecto

Las reuniones de proyecto permiten documentar discusiones, decisiones y action items.

## Crear Reunión

1. Ve al proyecto → Tab **Reuniones**
2. Click **"Nueva Reunión"**
3. Completa:
   - **Título** — Tema principal
   - **Fecha y hora**
   - **Tipo** — Daily, Planning, Review, Retro, etc.
   - **Participantes**
4. Guarda

## Tipos de Reunión

| Tipo | Propósito |
|------|-----------|
| **Daily** | Sincronización diaria (15 min) |
| **Planning** | Planificación de sprint |
| **Review** | Demo de lo completado |
| **Retrospective** | Mejora continua del equipo |
| **Kickoff** | Inicio de proyecto |
| **Other** | Cualquier otra reunión |

## Durante la Reunión

1. Click **"Iniciar"** cuando comience
2. Toma **notas** en el editor markdown
3. Registra **decisiones** tomadas
4. Crea **action items** con responsables
5. Click **"Finalizar"** al terminar

## Después

Los participantes pueden revisar la minuta y los action items quedan como tareas asignadas.`,
  },
  {
    slug: 'meetings-action-items',
    title: 'Action Items',
    module: 'meetings',
    category: 'Seguimiento',
    description: 'Cómo crear y dar seguimiento a tareas de reuniones',
    roles: ['superadmin', 'tenant_admin', 'admin', 'operator'],
    order: 2,
    keywords: ['action', 'item', 'tarea', 'seguimiento', 'asignado'],
    content: `# Action Items

Los action items son tareas que surgen de las reuniones y necesitan seguimiento.

## Crear Action Item

Durante o después de la reunión:
1. En la sección **Action Items**
2. Click **"Agregar"**
3. Define:
   - **Descripción** — Qué hay que hacer
   - **Responsable** — Quién lo hará
   - **Fecha límite** — Para cuándo
4. Se guarda automáticamente

## Estados

| Estado | Significado |
|--------|-------------|
| **Pending** | Pendiente de iniciar |
| **In Progress** | En proceso |
| **Done** | Completado |
| **Cancelled** | Ya no aplica |

## Seguimiento

### En la Reunión
Ve los action items pendientes de reuniones anteriores.

### Vista Global
Ve todos tus action items en **Calendario** → **Mis Tareas**.

### Notificaciones
Recibes recordatorio si un action item está por vencer.

## Tips

- Sé específico en la descripción
- Asigna siempre un responsable
- Define fechas realistas
- Revisa en la siguiente reunión`,
  },

  // ==========================================
  // QUOTATIONS
  // ==========================================
  {
    slug: 'quotations-create',
    title: 'Crear Cotización',
    module: 'quotations',
    category: 'General',
    description: 'Cómo crear propuestas comerciales',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 1,
    keywords: ['cotización', 'propuesta', 'precio', 'presupuesto', 'quote'],
    content: `# Crear Cotización

Las cotizaciones son propuestas comerciales formales para clientes.

## Crear Cotización

1. Ve a **Cotizaciones** en el menú
2. Click **"Nueva Cotización"**
3. Selecciona:
   - **Organización** — Cliente destino
   - **Proyecto** — Si aplica
4. Agrega **líneas de detalle**:
   - Descripción del servicio/producto
   - Cantidad
   - Precio unitario
5. Define condiciones:
   - **Vigencia** — Fecha de expiración
   - **Términos** — Condiciones de pago
   - **Descuentos** — Si aplican
6. Guarda como **Borrador**

## Líneas de Detalle

Cada línea puede ser:
- **Servicio** — Horas de desarrollo, consultoría
- **Producto** — Licencias, hardware
- **Otro** — Viáticos, extras

## Totales

El sistema calcula automáticamente:
- Subtotal
- Impuestos (configurable)
- Descuentos
- **Total final**

## Enviar

1. Revisa el borrador
2. Click **"Enviar al Cliente"**
3. El cliente recibe email con link al documento
4. Estado cambia a **Sent**`,
  },
  {
    slug: 'quotations-status',
    title: 'Estados de Cotización',
    module: 'quotations',
    category: 'Seguimiento',
    description: 'Flujo de estados de las cotizaciones',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 2,
    keywords: ['cotización', 'estado', 'aprobada', 'rechazada', 'flujo'],
    content: `# Estados de Cotización

Las cotizaciones pasan por diferentes estados durante su ciclo de vida.

## Estados

| Estado | Significado |
|--------|-------------|
| **Draft** | Borrador, en edición |
| **Sent** | Enviada al cliente |
| **Viewed** | Cliente abrió el documento |
| **Accepted** | Cliente aceptó la propuesta 🎉 |
| **Rejected** | Cliente rechazó |
| **Expired** | Pasó la fecha de vigencia |
| **Cancelled** | Cancelada internamente |

## Flujo Normal

\`\`\`
Draft → Sent → Viewed → Accepted → (Crear Orden de Venta)
                      → Rejected
                      → Expired
\`\`\`

## Después de Aceptar

Cuando el cliente acepta:
1. Se notifica al equipo
2. Puedes crear **Orden de Venta** desde la cotización
3. Los datos se copian automáticamente

## Métricas

- **Tasa de aceptación** — % de cotizaciones aceptadas
- **Tiempo de respuesta** — Días hasta decisión
- **Ticket promedio** — Valor promedio de cotizaciones`,
  },

  // ==========================================
  // SALES ORDERS
  // ==========================================
  {
    slug: 'sales-orders-overview',
    title: 'Órdenes de Venta',
    module: 'sales-orders',
    category: 'General',
    description: 'Gestión de órdenes de venta y facturación',
    roles: ['superadmin', 'tenant_admin', 'admin'],
    order: 1,
    keywords: ['orden', 'venta', 'factura', 'pedido', 'sales order'],
    content: `# Órdenes de Venta

Las órdenes de venta representan ventas confirmadas pendientes de facturar o entregar.

## Crear Orden de Venta

### Desde Cotización
1. Abre una cotización **Aceptada**
2. Click **"Crear Orden de Venta"**
3. Los datos se copian automáticamente
4. Revisa y confirma

### Manual
1. Ve a **Órdenes de Venta**
2. Click **"Nueva Orden"**
3. Completa datos del cliente y líneas
4. Guarda

## Campos

- **Cliente** — Organización compradora
- **Líneas** — Productos/servicios vendidos
- **Totales** — Subtotal, impuestos, total
- **Términos de pago** — Contado, crédito, etc.
- **Fecha de entrega** — Compromiso de entrega

## Estados

| Estado | Significado |
|--------|-------------|
| **Pending** | Pendiente de procesar |
| **Processing** | En preparación |
| **Delivered** | Entregado al cliente |
| **Invoiced** | Facturado |
| **Paid** | Pagado completamente |
| **Cancelled** | Cancelada |

## Seguimiento

Desde la orden puedes:
- Ver estado de entrega
- Registrar pagos
- Generar factura (integración externa)`,
  },

  // ==========================================
  // ADMIN
  // ==========================================
  {
    slug: 'admin-users',
    title: 'Gestión de Usuarios',
    module: 'admin',
    category: 'Usuarios',
    description: 'Cómo administrar usuarios y sus permisos',
    roles: ['superadmin', 'tenant_admin'],
    order: 1,
    keywords: ['usuario', 'admin', 'permisos', 'invitar', 'rol'],
    content: `# Gestión de Usuarios

Administra los usuarios que tienen acceso a tu tenant.

## Ver Usuarios

1. Ve a **Administración** → **Usuarios**
2. Lista todos los usuarios con:
   - Nombre y email
   - Rol asignado
   - Estado (activo/inactivo)
   - Último acceso

## Invitar Usuario

1. Click **"Invitar Usuario"**
2. Ingresa el email
3. Selecciona el rol:
   - **Admin** — Acceso completo
   - **Operator** — Gestión de proyectos
   - **Client** — Acceso limitado a sus proyectos
4. Envía invitación

El usuario recibe un email para crear su cuenta.

## Roles y Permisos

| Rol | Permisos |
|-----|----------|
| **Tenant Admin** | Todo + configuración del tenant |
| **Admin** | Proyectos, usuarios, reportes |
| **Operator** | Proyectos, requerimientos, docs |
| **Client** | Ver sus proyectos, crear tickets |

## Editar Usuario

1. Click en el usuario
2. Modifica rol o estado
3. Guarda

## Desactivar Usuario

Para remover acceso sin eliminar historial:
1. Edita el usuario
2. Cambia estado a **Inactivo**
3. El usuario no podrá iniciar sesión`,
  },
  {
    slug: 'admin-settings',
    title: 'Configuración del Tenant',
    module: 'admin',
    category: 'Configuración',
    description: 'Personaliza la configuración de tu workspace',
    roles: ['superadmin', 'tenant_admin'],
    order: 2,
    keywords: ['configuración', 'settings', 'tenant', 'personalizar'],
    content: `# Configuración del Tenant

Personaliza cómo funciona Torcaza Projects para tu organización.

## Datos del Tenant

- **Nombre** — Nombre de tu empresa/equipo
- **Logo** — Aparece en el sidebar y documentos
- **Dominio** — URL personalizada (opcional)

## Configuración de Proyectos

- **Prefijo por defecto** — Para keys de proyectos
- **Estados personalizados** — Agrega estados a tu flujo
- **Campos personalizados** — Campos extra en requerimientos

## Integraciones

Conecta con servicios externos:
- **Slack** — Notificaciones en canales
- **GitHub** — Link de commits y PRs
- **Calendar** — Sincronización de eventos

## Notificaciones

Configura qué notificaciones enviar:
- Email de nuevos tickets
- Recordatorios de reuniones
- Alertas de SLA

## Facturación

- Plan actual
- Uso (usuarios, proyectos, storage)
- Información de pago

## Acceso

Solo usuarios con rol **Tenant Admin** pueden acceder a esta sección.`,
  },

  // ==========================================
  // COPILOT
  // ==========================================
  {
    slug: 'copilot-overview',
    title: 'Asistente Copilot',
    module: 'copilot',
    category: 'General',
    description: 'Cómo usar el asistente IA de Torcaza Projects',
    order: 1,
    keywords: ['copilot', 'ia', 'asistente', 'inteligencia artificial', 'chat'],
    content: `# Copilot

Copilot es el asistente de IA integrado en Torcaza Projects que te ayuda con diversas tareas.

## Acceder a Copilot

- Click en el ícono de **robot** en el sidebar
- O usa el atajo **Ctrl + J** / **Cmd + J**

## Qué puede hacer

### Buscar Información
- "¿Cuáles son los tickets abiertos del proyecto X?"
- "¿Qué requerimientos tiene asignados Juan?"
- "Muéstrame los proyectos activos"

### Ayudar con Tareas
- "Genera una descripción para este requerimiento"
- "Crea criterios de aceptación para esta historia"
- "Resume las notas de la última reunión"

### Responder Preguntas
- "¿Cómo creo una épica?"
- "¿Qué significa el estado Code Review?"
- "¿Cómo comparto un documento?"

## Contexto

Copilot tiene acceso a:
- Tus proyectos y requerimientos
- Documentación del sistema
- Historial de la conversación

## Limitaciones

- No puede ejecutar acciones (crear, editar, eliminar)
- No accede a información de otros tenants
- Puede cometer errores, siempre verifica`,
  },
  {
    slug: 'copilot-prompts',
    title: 'Prompts Útiles para Copilot',
    module: 'copilot',
    category: 'Tips',
    description: 'Ejemplos de prompts efectivos para el asistente',
    order: 2,
    keywords: ['copilot', 'prompt', 'ejemplo', 'consulta', 'pregunta'],
    content: `# Prompts Útiles

Ejemplos de cómo aprovechar al máximo Copilot.

## Búsquedas

\`\`\`
"Dame un resumen del proyecto [nombre]"
"¿Cuántos bugs abiertos tenemos?"
"Lista las reuniones de esta semana"
"¿Qué tareas tiene pendientes [usuario]?"
\`\`\`

## Generación de Contenido

\`\`\`
"Genera user story para [funcionalidad]"
"Escribe criterios de aceptación para [requerimiento]"
"Crea descripción de release para versión 1.2.0"
"Sugiere estructura de documento de arquitectura"
\`\`\`

## Análisis

\`\`\`
"¿Cuál es el progreso del sprint actual?"
"¿Qué épicas están bloqueadas?"
"Compara velocidad de los últimos 3 sprints"
"¿Cuáles tickets están cerca de vencer SLA?"
\`\`\`

## Tips para Buenos Prompts

1. **Sé específico** — Incluye nombres de proyectos, fechas
2. **Da contexto** — "Para el proyecto X..."
3. **Pide formato** — "En una tabla", "como lista"
4. **Itera** — Si la respuesta no es perfecta, refina tu pregunta

## Atajos

- **/** — Ver comandos disponibles
- **↑** — Repetir último prompt
- **Esc** — Cerrar panel`,
  },

  // ==========================================
  // SUPER ADMIN
  // ==========================================
  {
    slug: 'superadmin-tenants',
    title: 'Gestión de Tenants',
    module: 'super-admin',
    category: 'Tenants',
    description: 'Administración de tenants en la plataforma',
    roles: ['superadmin'],
    order: 1,
    keywords: ['superadmin', 'tenant', 'workspace', 'crear', 'administrar'],
    content: `# Gestión de Tenants

Como Super Admin, puedes crear y administrar todos los tenants de la plataforma.

## Ver Tenants

1. Ve a **Super Admin** → **Tenants**
2. Lista todos los tenants:
   - Nombre
   - Plan actual
   - Usuarios activos
   - Estado

## Crear Tenant

1. Click **"Nuevo Tenant"**
2. Completa:
   - Nombre de la empresa
   - Email del admin inicial
   - Plan a asignar
3. Se crea el tenant y se invita al admin

## Editar Tenant

- Cambiar plan
- Modificar límites (usuarios, proyectos, storage)
- Ver estadísticas de uso
- Acceder como soporte

## Suspender Tenant

Para tenants con problemas de pago o violaciones:
1. Edita el tenant
2. Cambia estado a **Suspended**
3. Los usuarios verán mensaje de cuenta suspendida

## Eliminar Tenant

⚠️ **Acción irreversible**
1. Confirma que no hay datos importantes
2. El tenant debe estar vacío o archivado
3. Requiere doble confirmación`,
  },
  {
    slug: 'superadmin-apps',
    title: 'Gestión de Aplicaciones',
    module: 'super-admin',
    category: 'Aplicaciones',
    description: 'Administrar aplicaciones y versiones del sistema',
    roles: ['superadmin'],
    order: 2,
    keywords: ['superadmin', 'aplicación', 'versión', 'changelog', 'sistema'],
    content: `# Gestión de Aplicaciones

Administra las aplicaciones del ecosistema Torcaza y sus versiones.

## Aplicaciones

Las aplicaciones representan productos de software:
- Torcaza Projects (esta plataforma)
- Otras apps del ecosistema
- Apps de clientes (opcional)

## Ver Aplicaciones

1. **Super Admin** → **Aplicaciones**
2. Lista de apps con:
   - Nombre y descripción
   - Versión actual
   - Última actualización

## Gestionar Versiones

### Agregar Versión
1. Abre la aplicación
2. Tab **Versiones**
3. Click **"Nueva Versión"**
4. Define:
   - Número de versión
   - Tipo (release, hotfix, patch)
   - Notas de cambios
5. Guarda

### Historial
El historial de versiones alimenta el **Centro de Ayuda** → **Historial de Cambios**.

## Documentación

Cada aplicación puede tener:
- Documentos técnicos
- Manuales de usuario
- Guías de integración

Estos documentos pueden vincularse a las guías del Centro de Ayuda.`,
  },
]
